import { Modal, notification, Row, Popover } from 'antd';
import PageHeader from 'components/PageHeader';
import './webhookModal.scss';
import './webhookHistory.scss';
import { GET_WEBHOOK_HISTORY } from 'graphql/queries/webhookQueries';
import { useLazyQuery, useMutation } from '@apollo/client';
import { WebhookFilters } from './webhookFilter';
import { ReloadedIcon } from 'components/Icons';
import { WebhookHistoryTable } from './webhookHistoryTable';
import { useEffect, useRef, useState } from 'react';
import { RESEND_WEBHOOK } from 'graphql/mutations/webhookMutations';
import moment from 'moment';

export const WebhookHistory = ({ isOpen, onCancel }) => {
  const timeoutRef = useRef(null);
  const filterRef = useRef(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const PAGINATION_LIMIT = 10;
  const [
    getWebhookHistory,
    {
      data: { fetchWebhookEvents: { events = [], nextCursor, hasMore } = {} } = {},
      loading,
      fetchMore,
    },
  ] = useLazyQuery(GET_WEBHOOK_HISTORY, {
    variables: {
      filter: {},
      pagination: { limit: PAGINATION_LIMIT },
    },
    fetchPolicy: 'network-only',
  });

  const [resendWebhook] = useMutation(RESEND_WEBHOOK);

  const webhookColumn = [
    {
      title: 'Name',
      dataIndex: 'eventName',
      key: 'eventName',
      onCell: () => ({
        className: 'name-cell', // custom class for this column's td
      }),
      render: (text, record) => (
        <Row className="webhook-name" style={{ gap: '4px', flexDirection: 'column' }}>
          {record.isFake ? (
            <>
              <div
                className="skelton skelton-text"
                style={{ width: '120px', height: '16px' }}></div>
              <div className="skelton skelton-text" style={{ width: '80px', height: '12px' }}></div>
            </>
          ) : (
            <>
              <span className="">{record.eventName}</span>
              <span className="id-text">ID:{record._id}</span>
            </>
          )}
        </Row>
      ),
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      onCell: () => ({
        className: 'events-cell', // custom class for this column's td
      }),
      render: (text, record) =>
        record.isFake ? (
          <div className="skelton skelton-text" style={{ width: '100px', height: '16px' }}></div>
        ) : (
          <EventList events={record.events} />
        ),
    },
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      onCell: () => ({
        className: 'timestamp-cell', // custom class for this column's td
      }),
      render: (text, record) =>
        record.isFake ? (
          <div className="skelton skelton-text" style={{ width: '140px', height: '16px' }}></div>
        ) : (
          <span>{moment(record.timestamp).format('MMM DD, YYYY, hh:mm A')}</span>
        ),
    },
    {
      title: 'Proposal number',
      dataIndex: 'pnum',
      key: 'pnum',
      width: '155px',
      onCell: () => ({
        className: 'pnum-cell', // custom class for this column's td
      }),
      render: (text, record) =>
        record.isFake ? (
          <div className="skelton skelton-text" style={{ width: '80px', height: '16px' }}></div>
        ) : (
          <span>{record.pnum}</span>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      onCell: () => ({
        className: 'status-cell', // custom class for this column's td
      }),
      render: (text, record) =>
        record.isFake ? (
          <div
            className="skelton skelton-text"
            style={{ width: '60px', height: '16px', margin: '0 auto' }}></div>
        ) : (
          <Row
            justify="center"
            align="middle"
            className={`status-wrapper event-${record.status?.toLowerCase()}`}>
            <div className="dot"></div>
            <span>
              {record.status?.charAt(0).toUpperCase() + record.status?.slice(1).toLowerCase()}
            </span>
          </Row>
        ),
    },
    {
      title: '',
      dataIndex: '',
      key: '',
      onCell: () => ({
        className: 'action-cell', // custom class for this column's td
      }),
      align: 'right',
      render: (text, record) =>
        record.isFake ? (
          <div
            className="skelton skelton-text"
            style={{ width: '20px', height: '16px', margin: '0 auto' }}></div>
        ) : (
          <Popover
            content="Resend"
            trigger="hover"
            placement="top"
            overlayClassName="resend-webhook-popover">
            <div className="resend-webhook-btn" onClick={() => onResend([record._id])}>
              <ReloadedIcon />
            </div>
          </Popover>
        ),
    },
  ];

  useEffect(() => {
    getWebhookHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResend = (resendIds) => {
    resendWebhook({
      variables: {
        webhookIds: resendIds,
      },
      onCompleted: () => {
        notification.success({
          message: 'Webhook resend initiated',
          description: (
            <span>
              Please click <b>Refresh</b> to check the updated status.
            </span>
          ),
        });
      },
    });
  };

  const onFilterChange = (searchValue, date, filterItems) => {
    const filter = {};

    if (searchValue) {
      filter.webhookName = searchValue;
    }

    if (date.length) {
      filter.fromDate = date[0].startOf('day').toDate();
      filter.toDate = date[1].endOf('day').toDate();
    }

    const selectedStatusFilter = filterItems.status.filter((item) => item.checked);
    const selectedEventsFilter = filterItems.events.filter((item) => item.checked);
    if (selectedStatusFilter.length) {
      filter.status = selectedStatusFilter.map((item) => item.key);
    }
    if (selectedEventsFilter.length) {
      filter.events = selectedEventsFilter.map((item) => item.id);
    }

    filterRef.current = filter;
    timeoutRef.current = setTimeout(() => {
      getWebhookHistory({
        variables: {
          filter: filterRef.current,
          pagination: {
            limit: PAGINATION_LIMIT,
          },
        },
      });
    }, 1000);

    return () => clearTimeout(timeoutRef.current); // cleanup old timer
  };

  const onRefresh = () => {
    setIsRefreshing(true);
    getWebhookHistory({
      variables: {
        filter: filterRef.current,
        pagination: { limit: PAGINATION_LIMIT },
      },
    })
      .then(() => {
        setIsRefreshing(false);
      })
      .catch(() => {
        setIsRefreshing(false);
      });
  };

  const loadMore = () => {
    fetchMore({
      variables: {
        filter: filterRef.current,
        pagination: { limit: PAGINATION_LIMIT, cursor: nextCursor },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          ...prev,
          fetchWebhookEvents: {
            ...fetchMoreResult.fetchWebhookEvents,
            events: [
              ...prev.fetchWebhookEvents.events,
              ...fetchMoreResult.fetchWebhookEvents.events,
            ],
          },
        };
      },
    });
  };

  return isOpen ? (
    <Modal
      centered
      className="webhook-history-modal"
      visible={isOpen}
      width={'auto'}
      onCancel={onCancel}
      maskClosable={false}
      footer={false}>
      <Row>
        <PageHeader pageTitle="Webhook history" pageLabel="Limited to the last 14 days" />
      </Row>
      <WebhookFilters
        onFilterChange={onFilterChange}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
      />
      <WebhookHistoryTable
        data={events || []}
        columns={webhookColumn}
        loading={loading}
        hasMore={hasMore}
        loadMore={loadMore}
        resendAll={onResend}
      />
    </Modal>
  ) : (
    <></>
  );
};

export const EventList = ({ events = [] }) => {
  if (!events || events.length === 0) {
    return <div className="skelton skelton-text" style={{ width: '100px', height: '16px' }}></div>;
  }

  const maxVisible = 3;
  const visibleEvents = events.slice(0, maxVisible);
  const remainingCount = events.length - maxVisible;

  const remainingEvents = events.slice(maxVisible);

  const popoverContent = (
    <div className="event-list-popover">
      <span>{remainingEvents.join('; ')}</span>
    </div>
  );

  return (
    <div className="event-list-count-container">
      <span>{visibleEvents.join('; ')}</span>
      {remainingCount > 0 && (
        <Popover
          content={popoverContent}
          trigger="hover"
          placement="top"
          overlayClassName="event-list-popover-overlay">
          <span className="remaining-count">+{remainingCount}</span>
        </Popover>
      )}
    </div>
  );
};
